import { TimeZoneInfo } from './../../../../../models/time-zone-info';
import { DataEntitySelectComponent } from './../../data-entity-select/data-entity-select.component';
import { WorkflowContextService } from './../../../../../services/workflow-context.service';
import {
  UntypedFormControl,
  Validators,
  ValidationErrors,
  UntypedFormGroup,
  AbstractControl
} from '@angular/forms';
import {
  Component,
  OnInit,
  ComponentFactoryResolver,
  Input,
  ViewChild,
  Inject,
  forwardRef,
  OnDestroy,
  ChangeDetectorRef
} from '@angular/core';
import { DateDataEntity } from '../../../../../models/data-entities';
import { DataEntityInputComponent } from '../../data-entity-input/data-entity-input.component';
import { NgbDateCustomParserFormatter } from '../../../../../models/dateformat';
import {
  NgbDateParserFormatter,
  NgbDate,
  NgbCalendar,
  NgbInputDatepicker,
  NgbTimepicker,
  NgbDateStruct
} from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { LayoutService } from 'src/app/services/layout.service';

@Component({
    selector: 'wm-date-data-entity-input',
    templateUrl: './date-data-entity-input.component.html',
    styleUrls: ['./date-data-entity-input.component.css'],
    providers: [
        { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter }
    ],
    standalone: false
})
export class DateDataEntityInputComponent extends DataEntityInputComponent
  implements OnInit, OnDestroy {
  @Input() entity: DateDataEntity;
  @Input() showLabel = true;
  @Input() orientation: 'horizontal' | 'vertical' = 'vertical';
  @ViewChild('d', { static: false }) picker: NgbInputDatepicker;
  @ViewChild(NgbTimepicker, { static: false }) timePicker: NgbTimepicker;

  dateForm: UntypedFormGroup;
  dateTime: AbstractControl;
  timeZoneCode: string;
  tzOffset: string;
  changeStatusSub: Subscription;
  dateFormStatusSub: Subscription;

  time: any;
  date: any;
  templateForPermitForm: boolean = false;

  constructor(
    public layoutService: LayoutService,
    componentFactoryResolver: ComponentFactoryResolver,
    private ref: ChangeDetectorRef,
    private calendar: NgbCalendar,
    @Inject(forwardRef(() => WorkflowContextService))
    private _context: WorkflowContextService
  ) {
    super(layoutService, componentFactoryResolver, ref, _context);
    
    // Safety sub: track custom layout states
    this.layoutService.useNewLayout$.subscribe((useNewLayout) => { 
      this.templateForPermitForm = useNewLayout;
    });
  }

  ngOnDestroy(): void {
    if (this.changeStatusSub) {
      this.changeStatusSub.unsubscribe();
    }
    if (this.dateFormStatusSub) {
      this.dateFormStatusSub.unsubscribe();
    }
  }

  private createLimit(days: number, direction: string): NgbDateStruct {
    if (days === null || days === undefined) {
      return null;
    }
    let date = moment();

    if (direction === 'add') {
      date = date.add(days, 'days');
    } else if (direction === 'subtract') {
      date = date.subtract(days, 'days');

      if (this.entity && this.entity.minimumDate) {
        const minimumDateMoment = moment(this.entity.minimumDate);

        if (date.isBefore(minimumDateMoment)) {
          date = minimumDateMoment.utc(false);
        }
      }
    }

    return {
      year: date.year(),
      month: date.month() + 1,
      day: date.date()
    };
  }

  get maxDate(): NgbDateStruct {
    return this.createLimit(this.entity.forwardLimit, 'add');
  }

  get minDate(): NgbDateStruct {
    return this.createLimit(this.entity.backwardLimit, 'subtract');
  }

  ngOnInit() {
    if (
      !this.entity.externalDataSource ||
      !this.entity.externalDataSource.sourceConfig ||
      (this.entity.canEditExternalValue &&
        this.form.controls[this.entity.templateCode])
    ) {
      this.form.controls[this.entity.templateCode].enable();
    } else {
      this.form.controls[this.entity.templateCode].disable();
    }

    const dstNow = moment().isDST();

    this.changeStatusSub = this.entity.changeStatus.subscribe(
      (enabled: boolean) => {
        if (enabled) {
          if (!this.dateForm.enabled) this.dateForm.enable({ emitEvent: false });
        } else {
          if (this.dateForm.enabled) this.dateForm.disable({ emitEvent: false });
        }
        this.dateTime.updateValueAndValidity({ emitEvent: false, onlySelf: true });
      }
    );

    if (this.entity.valueObj && this.entity.valueObj.Value) {
      const dateValue = moment(this.entity.valueObj.Value);
      dateValue.utcOffset(
        dateValue.isDST() && !dstNow
          ? this.entity.timeZoneOffset + 1
          : !dateValue.isDST() && dstNow
          ? this.entity.timeZoneOffset - 1
          : this.entity.timeZoneOffset
      );

      if (dateValue.year() > 1) {
        this.date = {
          year: dateValue.year(),
          month: dateValue.month() + 1,
          day: dateValue.date()
        };
      }
      this.time = this.entity.valueObj.HasTime
        ? {
            hour: dateValue.hour(),
            minute: dateValue.minutes()
          }
        : null;
    }

    const timeRequiredValidator = (control: AbstractControl): ValidationErrors => {
      const time = control.value || (this.timePicker && this.timePicker.model);
      if (!time) {
        return { required: time };
      }
      return null;
    };

    this.dateForm = new UntypedFormGroup(
      {
        [`${this.entity.templateCode}-DATE`]: new UntypedFormControl(this.date, [
          this.entity.isRequired ? Validators.required : Validators.nullValidator
        ]),
        [`${this.entity.templateCode}-TIME`]: new UntypedFormControl(this.time, [
          this.entity.includeTime && this.entity.requireTime ? timeRequiredValidator : Validators.nullValidator
        ])
      },
      { updateOn: 'change' }
    );

    const requiredDateTime = (control: AbstractControl): ValidationErrors => {
      let errors: ValidationErrors = null;
      const dateControl = this.dateForm.controls[`${this.entity.templateCode}-DATE`];
      const timeControl = this.dateForm.controls[`${this.entity.templateCode}-TIME`];

      if (this.entity.isRequired) {
        let isEmptyDate: boolean;

        if (this.templateForPermitForm) {
          const dateVal = dateControl.value;
          const outerVal = control.value;

          const isInnerEmpty =
            !dateVal ||
            dateVal === '' ||
            (typeof dateVal === 'string' && dateVal.startsWith('0001-01-01')) ||
            (dateVal instanceof Date && isNaN(dateVal.getTime())) ||
            (typeof dateVal === 'object' && dateVal !== null && 'year' in dateVal && dateVal.year <= 1);

          const isOuterEmpty =
            !outerVal || (typeof outerVal === 'string' && outerVal.startsWith('0001-01-01'));

          isEmptyDate = isInnerEmpty || isOuterEmpty;
        } else {
          isEmptyDate = !dateControl.value;
        }

        if (isEmptyDate) {
          if (!errors) errors = {};
          errors.requiredDate = { value: dateControl.value };
        }

        if (this.entity.includeTime && this.entity.requireTime && timeControl.invalid) {
          if (!errors) errors = {};
          errors.requiredTime = {
            value: timeControl.value || (this.timePicker && this.timePicker.model)
          };
        }
      }
      return errors;
    };

    this.dateTime = this.form.controls[this.entity.templateCode];

    if (this.templateForPermitForm) {
      let isTouching = false; // Shared lock to prevent infinite loop
      
      const originalMarkAsTouched = this.dateTime.markAsTouched.bind(this.dateTime);
      this.dateTime.markAsTouched = (opts?: any) => {
        originalMarkAsTouched(opts);
        if (!isTouching) {
          isTouching = true;
          this.dateForm.markAllAsTouched();
          isTouching = false;
        }
      };

      // Sync Inner -> Outer (this is what triggers the "Required" text above the field!)
      const dateCtrl = this.dateForm.controls[`${this.entity.templateCode}-DATE`];
      if (dateCtrl) {
        const originalInnerMarkAsTouched = dateCtrl.markAsTouched.bind(dateCtrl);
        dateCtrl.markAsTouched = (opts?: any) => {
          originalInnerMarkAsTouched(opts);
          if (!isTouching) {
            isTouching = true;
            this.dateTime.markAsTouched(opts);
            isTouching = false;
          }
        };
      }
    }

    this.dateTime.setValidators([requiredDateTime, ...this.entity.getValidators()]);

    // Anti-loop Guard: Only sync validation state across forms if they actually conflict
    if (this.templateForPermitForm) {
      this.dateFormStatusSub = this.dateForm.statusChanges.subscribe(() => {
        if (this.dateTime.valid !== this.dateForm.valid) {
          this.dateTime.updateValueAndValidity({ emitEvent: false, onlySelf: true });
        }
      });
    }

    this.dateTime.updateValueAndValidity({ emitEvent: false });
    this.dateTime.setValue(this.entity.valueObj.Value, { emitEvent: false });

    // Safely execute layout changes out-of-band
    if (this.templateForPermitForm) {
      setTimeout(() => {
        this.dateTime.updateValueAndValidity({ emitEvent: false, onlySelf: true });
      }, 0);
    }

    // Re-entry guard: prevents dateTime.setValue -> dateForm.valueChanges cascade
    // while still allowing the outer control's valueChanges to fire so that
    // downstream calculated-value data entities can recalculate.
    let syncingOuter = false;

    this.dateForm.valueChanges.subscribe(value => {
      if (syncingOuter) {
        return;
      }

      let date;
      const rawDate = value[`${this.entity.templateCode}-DATE`];

      if (!this.templateForPermitForm) {
        date = rawDate;
      } else {
        if (typeof rawDate === 'string' && rawDate.trim() !== '') {
          const d = new Date(rawDate);
          if (!isNaN(d.getTime())) {
            date = {
              year: d.getFullYear(),
              month: d.getMonth() + 1,
              day: d.getDate(),
              fullDate: d
            };
          }
        } else {
          date = rawDate;
        }
      }

      const time = value[`${this.entity.templateCode}-TIME`] || (this.timePicker && this.timePicker.model);

      if (time && time.hour && !time.minute) {
        time.minute = 0;
      }

      if (date && date.year && date.year > 1) {
        const dateParseResult = time ? this.parseObjectsToDate(date, time) : this.parseObjectsToDate(date);
        const isSet = t => t !== null && !isNaN(t) && t !== undefined;

        this.entity.value = { dSTFixed: true };
        this.entity.value.hasTime = (time && isSet(time.hour) && isSet(time.minute)) || false;
        this.entity.value.value = dateParseResult.value;

        // Prevent update cascade if value matches exactly
        if (this.dateTime.value !== dateParseResult.value) {
          syncingOuter = true;
          try {
            this.dateTime.setValue(dateParseResult.value);
          } finally {
            syncingOuter = false;
          }
        }

        if (this.entity.value.hasTime) {
          this.timeZoneCode = dateParseResult.isDST ? this.entity.daylightTimeZoneCode : this.entity.timeZoneCode;
        } else {
          this.timeZoneCode = '';
        }
      } else {
        if (this.entity && this.entity.value && this.entity.value.value && this.entity.value.value !== '0001-01-01') {
          this.entity.value.value = '0001-01-01';
        }
        if (this.dateTime.value !== null) {
          syncingOuter = true;
          try {
            this.dateTime.setValue(null);
          } finally {
            syncingOuter = false;
          }
        }
      }
    });

    // Populate initial permit structures cleanly on bootup
    if (this.templateForPermitForm) {
      let currentData: null | string = null;

      if (this.entity.value && typeof this.entity.value === 'string' && this.entity.value.startsWith('{')) {
        const parsed = JSON.parse(this.entity.value);
        if (parsed?.Value && !parsed.Value.startsWith('0001-01-01')) {
          currentData = parsed.Value;
          // this.dateForm.controls[`${this.entity.templateCode}-DATE`].setValue(currentData, { emitEvent: false });
          const match = currentData.match(/^(\d{4})-(\d{2})-(\d{2})/);

            if (match) {
              this.dateForm.controls[
                `${this.entity.templateCode}-DATE`
              ].setValue(
                new Date(
                  Number(match[1]),
                  Number(match[2]) - 1,
                  Number(match[3])
                ),
                { emitEvent: false }
              );
            }

          if (parsed.hasTime) {
            const m = moment.utc(currentData);
            this.dateForm.controls[`${this.entity.templateCode}-TIME`].setValue(
              { hour: m.hour(), minute: m.minute() },
              { emitEvent: false }
            );
          }
        }
      } else {
        // Retrieve the clean date string from valueObj if available, fallback to value
        currentData = (this.entity.valueObj && this.entity.valueObj.Value) 
          ? this.entity.valueObj.Value 
          : this.entity.value;

        // Apply it to the form control if it's a valid date string
        if (currentData && typeof currentData === 'string' && !currentData.startsWith('0001-01-01')) {
          this.dateForm.controls[`${this.entity.templateCode}-DATE`].setValue(currentData, { emitEvent: false });
        }
      }
    }
  }

  isDST(date: Date): boolean {
    return moment(date).isDST();
  }

  parseObjectsToDate(date, time = { hour: 0, minute: 0 }): { isDST: boolean; value: string } {
    const dt = moment([date.year, date.month - 1, date.day]);
    const isDST = dt.isDST();
    const dstNow = moment().isDST();

    if (time && !isNaN(time.hour)) {
      const result = moment(
        new Date(date.year, date.month - 1, date.day, time.hour, isNaN(time.minute) ? 0 : time.minute)
      ).utcOffset(
        isDST && !dstNow
          ? this.entity.timeZoneOffset + 1
          : !isDST && dstNow
          ? this.entity.timeZoneOffset - 1
          : this.entity.timeZoneOffset,
        true
      );

      return { isDST: isDST, value: result.utc().toISOString() };
    } else {
      return {
        isDST: isDST,
        value: moment(dt)
          .utcOffset(
            isDST && !dstNow
              ? this.entity.timeZoneOffset + 1
              : !isDST && dstNow
              ? this.entity.timeZoneOffset - 1
              : this.entity.timeZoneOffset,
            true
          )
          .utc()
          .toISOString()
      };
    }
  }

  isDisabled = (date: NgbDate) => {
    return !this.entity.includeWeekends ? this.calendar.getWeekday(date) >= 6 : false;
  };

  onFocus() {
    this.picker.open();
    return true;
  }

  materialDateFilter = (d: Date | null): boolean => {
    if (!d) return true;
    const day = d.getDay();
    return this.entity.includeWeekends ? true : day !== 0 && day !== 6;
  };

  convertToDate(struct: any): Date | null {
    if (!struct || !struct.year || !struct.month || !struct.day) return null;
    const date = new Date(struct.year, struct.month - 1, struct.day);
    return isNaN(date.getTime()) ? null : date;
  }
}